import React from 'react';
import AgreementPageLayout from '../../components/AgreementPageLayout';

const ProfessionalAccountAgreementPage: React.FC = () => {
  return (
    <AgreementPageLayout
      title="AACore Professional / Service Provider Account Agreement"
      effectiveDate="01/01/2026"
      lastUpdated="06/10/2026"
      toc={[
        { id: 'operator-contact', title: 'Operator & Contact' },
        { id: 'invitation-only', title: '1) Invitation Only; Sponsoring Business Control' },
        { id: 'acceptance-relationship', title: '2) Acceptance; Relationship to Other Agreements' },
        { id: 'definitions', title: '3) Definitions' },
        { id: 'trial-period', title: '4) Free Trial, Enhancement Period, and Use at Your Own Risk' },
        { id: 'platform-only', title: '5) Platform Only; AAI Does Not Provide or Supervise Services' },
        { id: 'no-employment-agency', title: '6) No Employment, Agency, Partnership, or Payroll Relationship With AAI' },
        { id: 'professional-responsibilities', title: '7) Professional Responsibilities; Safety, Quality, Licensing, and Compliance' },
        { id: 'business-policies', title: '8) Sponsoring Business Policies and Customer Relationship' },
        { id: 'account-security', title: '9) Account Security; Authorized Use Only' },
        { id: 'customer-information', title: '10) Customer Information, Confidentiality, and Data Handling' },
        { id: 'sensitive-data', title: '11) Sensitive Data Restrictions' },
        { id: 'payments-stripe', title: '12) Payments, Tips, Deposits, Stripe, and Money-Related Information' },
        { id: 'messaging', title: '13) Messaging, Email, SMS, and Customer Communications' },
        { id: 'content', title: '14) Content You Provide; Photos, Notes, Descriptions, and Messages' },
        { id: 'prohibited-conduct', title: '15) Prohibited Conduct' },
        { id: 'third-party-services', title: '16) Third-Party Services and Integrations' },
        { id: 'availability', title: '17) Availability, Maintenance, and No Uptime Commitment' },
        { id: 'disclaimers', title: '18) Disclaimers; As-Is Platform' },
        { id: 'suspension-termination', title: '19) Suspension and Termination of Professional Access' },
        { id: 'intellectual-property', title: '20) Intellectual Property' },
        { id: 'confidentiality-feedback', title: '21) Confidentiality and Feedback' },
        { id: 'indemnification', title: '22) Indemnification' },
        { id: 'limitation-liability', title: '23) Limitation of Liability' },
        { id: 'release', title: '24) Release' },
        { id: 'force-majeure', title: '25) Force Majeure' },
        { id: 'dispute-resolution', title: '26) Dispute Resolution; Arbitration; Class Action Waiver' },
        { id: 'governing-law', title: '27) Governing Law; Venue' },
        { id: 'changes-agreement', title: '28) Changes to Agreement' },
        { id: 'miscellaneous-contact', title: '29) Miscellaneous; Contact' },
        { id: 'agreement-acknowledgment', title: 'Agreement Acknowledgment' },
      ]}
    >
      <p>
        This Agreement governs invite-only Professional and Service Provider access to AACore, including AACore Booking.
        It clarifies that Professionals access the Platform through a Sponsoring Business, that A&amp;A Inomatic, LLC
        provides technology only, and that AAI does not employ, supervise, pay, license, or control Professionals or
        their services.
      </p>

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="font-semibold mb-1">Important Invite-Only, Trial, and Relationship Notice</div>
        Professional accounts are invitation-only and are controlled by the Business that invited you. AACore Booking is
        provided during an enhancement, testing, and trial period through December 31, 2026, unless A&amp;A Inomatic, LLC
        announces a different date or plan. The Platform is provided as a technology tool only and is used at your own
        risk. AAI is not your employer, contracting party for services, payroll provider, supervisor, payment processor,
        bank, insurance provider, licensing authority, or professional regulator.
      </div>

      <div id="operator-contact" className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <div className="font-semibold text-gray-900 mb-1">Operator &amp; Contact</div>
        <strong>Platform:</strong> AACore, including AACore Booking and related modules (the &ldquo;Platform&rdquo;)
        <br />
        <strong>Operator:</strong> A&amp;A Inomatic, LLC (&ldquo;AAI,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; &ldquo;our&rdquo;)
        <br />
        <strong>Support:</strong> <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>
        <br />
        <strong>Address:</strong> Clifton, VA 20124
        <br />
        <strong>Privacy Policy:</strong> <a href="/privacy">AACore Privacy Policy</a>
        <p className="mt-3 mb-0">
          This AACore Professional / Service Provider Account Agreement (&ldquo;Agreement&rdquo;) governs use of the
          Platform by any invited professional, staff member, service provider, practitioner, contractor, employee,
          representative, or other individual who accesses AACore through a Business account (&ldquo;Professional,&rdquo;
          &ldquo;you,&rdquo; &ldquo;your&rdquo;).
        </p>
      </div>

      <h3 id="invitation-only">1) Invitation Only; Sponsoring Business Control</h3>
      <p>
        Professional accounts cannot be created independently. You may access the Platform only if you are invited,
        authorized, or assigned by a Business that uses AACore (your &ldquo;Sponsoring Business&rdquo;).
      </p>
      <p>
        Your access, role, permissions, calendar visibility, customer visibility, services, locations, work schedule,
        messaging abilities, payment visibility, and feature access are controlled by the Sponsoring Business and may be
        changed, limited, suspended, or revoked by the Sponsoring Business at any time.
      </p>
      <p>
        AAI may also suspend, restrict, or terminate your access for security, legal, compliance, payment,
        customer-safety, abuse-prevention, or platform-integrity reasons.
      </p>

      <h3 id="acceptance-relationship">2) Acceptance; Relationship to Other Agreements</h3>
      <p>
        By accepting an invitation, clicking &ldquo;I Agree,&rdquo; signing in, accessing a Professional account,
        managing appointments, viewing Customer information, sending messages, or otherwise using the Platform, you agree
        to this Agreement and any posted policies referenced by the Platform.
      </p>
      <p>
        If you have a separate employment, contractor, vendor, commission, compensation, scheduling, non-compete,
        confidentiality, or service agreement with the Sponsoring Business, that agreement governs your relationship with
        the Sponsoring Business. AAI is not a party to that agreement, does not enforce it, and is not responsible for
        any obligations, payments, wages, commissions, tips, payroll, taxes, benefits, reimbursements, equipment,
        insurance, scheduling disputes, classification issues, discipline, or termination decisions between you and the
        Sponsoring Business.
      </p>
      <p>If you do not agree to this Agreement, do not accept the invitation and do not use the Platform.</p>

      <h3 id="definitions">3) Definitions</h3>
      <ul>
        <li>
          <strong>&ldquo;Business&rdquo;</strong> means a third-party business, provider, office, store, organization,
          owner, or account holder that uses AACore to manage bookings, Customers, staff, services, communications,
          payments, or related operations.
        </li>
        <li>
          <strong>&ldquo;Sponsoring Business&rdquo;</strong> means the Business that invited, authorized, or assigned
          you to access the Platform.
        </li>
        <li>
          <strong>&ldquo;Customer&rdquo;</strong> means an end user, client, guest, patient, consumer, or other person who
          books, requests, purchases, communicates with, or receives services from a Business or Professional.
        </li>
        <li>
          <strong>&ldquo;Services&rdquo;</strong> means any services, work, consultation, treatment, care, appointment,
          product, instruction, recommendation, or customer-facing activity provided or offered by a Business or
          Professional.
        </li>
        <li>
          <strong>&ldquo;Professional Data&rdquo;</strong> means information related to you or entered by you, including
          profile information, schedule availability, notes, messages, images, service records, appointment actions, and
          other Platform activity.
        </li>
        <li>
          <strong>&ldquo;Customer Information&rdquo;</strong> means names, contact information, appointment details,
          preferences, notes, messages, service history, payment status, and other data relating to Customers.
        </li>
        <li>
          <strong>&ldquo;Payment Processor&rdquo;</strong> means Stripe or another third-party payment provider used to
          process online payments, deposits, tips, refunds, payouts, disputes, chargebacks, identity verification, or
          related payment services.
        </li>
        <li>
          <strong>&ldquo;Company Parties&rdquo;</strong> means AAI and its owners, officers, directors, managers,
          employees, contractors, agents, affiliates, licensors, vendors, and service providers.
        </li>
        <li>
          <strong>&ldquo;Applicable Law&rdquo;</strong> means all laws, rules, regulations, licensing requirements,
          professional standards, privacy obligations, consumer protection rules, payment rules, labor rules, tax
          obligations, and industry requirements applicable to you, your services, the Sponsoring Business, Customers,
          or your Platform use.
        </li>
      </ul>

      <h3 id="trial-period">4) Free Trial, Enhancement Period, and Use at Your Own Risk</h3>
      <p>
        AACore Booking is currently being provided as part of a free enhancement, testing, and trial period through
        December 31, 2026, unless AAI provides a different written date, transitions to a paid plan, or ends free access
        earlier or later in its discretion.
      </p>
      <p>
        During the trial period, the Platform may contain defects, unfinished features, configuration changes, downtime,
        incorrect results, workflow changes, design changes, feature removals, or other issues. You use the Platform at
        your own risk and remain responsible for confirming appointments, Customer needs, service instructions, messages,
        payment status, and work obligations through appropriate backup procedures when needed.
      </p>
      <p>
        AAI may modify, suspend, limit, or discontinue any free, trial, beta, early access, or enhancement-period feature
        at any time without liability to you.
      </p>

      <h3 id="platform-only">5) Platform Only; AAI Does Not Provide or Supervise Services</h3>
      <p>
        AAI provides software tools for scheduling, booking, messaging, recordkeeping, business management, and related
        technology functions. AAI does not provide Services to Customers, does not supervise Professionals, does not
        inspect or verify service quality, does not control how Services are performed, and does not determine whether
        you are qualified, licensed, insured, or legally permitted to provide Services.
      </p>
      <p>
        Any service descriptions, prices, durations, notes, policies, forms, instructions, payment statuses, schedules, or
        Customer information displayed through the Platform are provided, entered, configured, or controlled by the
        Sponsoring Business, the Professional, the Customer, or third-party systems. AAI is not responsible for their
        accuracy, completeness, legality, timeliness, or suitability for any specific service situation.
      </p>

      <h3 id="no-employment-agency">6) No Employment, Agency, Partnership, or Payroll Relationship With AAI</h3>
      <p>
        AAI does not employ you, contract with you to provide Services, pay you, withhold taxes for you, provide benefits
        to you, control your work conditions, set your compensation, schedule your work, direct your Services, evaluate
        your performance, discipline you, or terminate your relationship with the Sponsoring Business.
      </p>
      <p>
        Nothing in this Agreement or your Platform access creates an employment, contractor, agency, partnership, joint
        venture, franchise, fiduciary, representative, broker, payment-agent, payroll-agent, or supervisory relationship
        between you and AAI.
      </p>
      <p>
        Any compensation, wages, commissions, tips, gratuities, reimbursements, payouts, bonuses, deductions, tax forms,
        employment classification, worker classification, benefits, insurance, and work authorization matters are solely
        between you and the Sponsoring Business or another applicable third party, not AAI.
      </p>

      <h3 id="professional-responsibilities">7) Professional Responsibilities; Safety, Quality, Licensing, and Compliance</h3>
      <p>
        You are solely responsible for your own conduct, judgment, services, actions, omissions, communications,
        compliance, and use of Customer Information. You agree to:
      </p>
      <ul>
        <li>
          provide Services only if you are properly authorized, trained, licensed, certified, insured, and legally
          permitted to do so;
        </li>
        <li>
          follow Applicable Law, professional standards, safety requirements, sanitation requirements, consumer protection
          rules, privacy obligations, and Sponsoring Business policies;
        </li>
        <li>
          provide accurate information about your availability, service status, appointment notes, Customer
          communications, and completion of work;
        </li>
        <li>
          provide appropriate disclosures, consent processes, intake review, aftercare instructions, warnings, and Customer
          communications when required;
        </li>
        <li>avoid unsafe, illegal, deceptive, discriminatory, harassing, negligent, or unprofessional conduct;</li>
        <li>
          immediately inform the Sponsoring Business if you are not properly licensed, trained, insured, authorized, or
          comfortable performing a Service; and
        </li>
        <li>verify that any Platform settings you can control match your actual availability and service responsibilities.</li>
      </ul>
      <p>
        If you are not properly authorized, licensed, certified, trained, or legally permitted to provide a Service, you
        must not perform that Service and must notify the Sponsoring Business promptly.
      </p>

      <h3 id="business-policies">8) Sponsoring Business Policies and Customer Relationship</h3>
      <p>
        The Sponsoring Business controls its Customer relationship, service offerings, pricing, taxes, deposits, refunds,
        cancellation rules, no-show rules, late-arrival rules, staff permissions, service locations, availability, intake
        processes, insurance, and customer-facing policies.
      </p>
      <p>
        You agree to follow the Sponsoring Business&apos;s policies and procedures when using the Platform. AAI is not
        responsible for resolving disagreements between you and the Sponsoring Business or between you and Customers.
      </p>
      <p>
        If you believe the Sponsoring Business&apos;s instructions, Customer request, or Platform configuration creates a
        safety, legal, licensing, privacy, payment, or professional risk, you must stop and escalate the issue to the
        Sponsoring Business before proceeding.
      </p>

      <h3 id="account-security">9) Account Security; Authorized Use Only</h3>
      <p>
        You are responsible for maintaining the confidentiality and security of your login credentials, verification
        codes, devices, browsers, and account sessions. You agree to:
      </p>
      <ul>
        <li>use only your own authorized account and not share your credentials or verification codes;</li>
        <li>not use another person&apos;s account or allow anyone else to use your account;</li>
        <li>use the Platform only for legitimate work authorized by the Sponsoring Business;</li>
        <li>promptly sign out of shared or public devices;</li>
        <li>
          promptly notify the Sponsoring Business and, when appropriate,{' '}
          <a href="mailto:support@aainomatic.com">support@aainomatic.com</a> if you suspect unauthorized access, account
          compromise, data exposure, or misuse; and
        </li>
        <li>comply with any security instructions issued by the Sponsoring Business or AAI.</li>
      </ul>
      <p>
        You are responsible for all actions taken under your account except to the extent caused solely by AAI&apos;s
        willful misconduct.
      </p>

      <h3 id="customer-information">10) Customer Information, Confidentiality, and Data Handling</h3>
      <p>
        You may access Customer Information only as necessary to deliver, document, schedule, support, or manage Services
        authorized by the Sponsoring Business. You must keep Customer Information confidential and must not access, use,
        copy, export, download, photograph, disclose, sell, share, or retain Customer Information except as authorized by
        the Sponsoring Business, permitted by AAI, and allowed by Applicable Law.
      </p>
      <p>
        You must not use Customer Information for personal purposes, outside marketing, independent solicitation,
        harassment, retaliation, competitive business activity, unauthorized follow-up, or any purpose outside your
        authorized role.
      </p>
      <p>
        When your access ends, you must stop using the Platform and must not retain Customer Information obtained through
        the Platform unless you are independently authorized by the Sponsoring Business and Applicable Law.
      </p>

      <h3 id="sensitive-data">11) Sensitive Data Restrictions</h3>
      <p>
        Unless AAI expressly agrees in writing and the Sponsoring Business has implemented appropriate controls, you must
        not enter, upload, request, store, or transmit through the Platform:
      </p>
      <ul>
        <li>full payment card numbers, CVV codes, magnetic stripe data, full bank account numbers, or full routing numbers;</li>
        <li>Social Security numbers, driver&apos;s license numbers, passport numbers, or government identity documents;</li>
        <li>
          medical records, HIPAA protected health information, diagnosis records, treatment records, biometric
          identifiers, or highly regulated sensitive data;
        </li>
        <li>information about minors beyond what is lawful, necessary, and properly authorized; or</li>
        <li>any information you or the Sponsoring Business are not legally permitted to collect or process.</li>
      </ul>
      <p>
        If you enter prohibited, excessive, inaccurate, or unauthorized data, you do so at your own risk and remain
        responsible for resulting claims, losses, regulatory issues, privacy issues, and consequences.
      </p>

      <h3 id="payments-stripe">12) Payments, Tips, Deposits, Stripe, and Money-Related Information</h3>
      <p>
        The Sponsoring Business may enable payment, deposit, invoice, gift card, refund, tip, or related payment
        workflows. If payment features are enabled, payment services are provided by a Payment Processor such as Stripe,
        not by AAI.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>No Stripe affiliation and no banking custody.</strong> AAI is not owned by, operated by, sponsored by,
          endorsed by, affiliated with, or acting as an agent of Stripe. AAI is not a bank, payment processor, money
          transmitter, escrow agent, merchant of record, financial institution, credit provider, payment facilitator,
          payment intermediary, payroll provider, tip distributor, employer of record, or fiduciary for funds. AAI does
          not hold, control, receive custody of, settle, distribute, or guarantee Customer funds, Business payouts,
          Professional pay, tips, deposits, refunds, reversals, or chargeback outcomes.
        </p>
      </div>
      <p>
        You must not request or collect full card numbers, CVV codes, full bank account numbers, or full routing numbers in
        appointment notes, messages, forms, images, or any non-processor field. Full payment card or banking information
        should be entered only into Payment Processor-controlled flows or hosted payment interfaces.
      </p>
      <p>
        Any Professional compensation, tips, gratuities, commissions, payroll, reimbursements, tax reporting, wage claims,
        payout allocation, tip pooling, tip distribution, deductions, refunds, chargebacks, or payment disputes are solely
        between you, the Sponsoring Business, the Customer, and any applicable Payment Processor. AAI is not responsible
        for payment processor downtime, payment failures, duplicate charges, declined cards, failed refunds, held funds,
        delayed payouts, reserves, identity verification failures, chargeback decisions, or disputes over Professional pay
        or tips.
      </p>
      <p>
        AAI may suspend or disable payment-related features or access if fraud, abuse, excessive disputes, processor
        restrictions, suspicious activity, data misuse, or violations are suspected.
      </p>

      <h3 id="messaging">13) Messaging, Email, SMS, and Customer Communications</h3>
      <p>
        The Platform may allow you to send or receive transactional messages, appointment updates, notes, reminders,
        support communications, and other service-related communications. You agree to use messaging only for legitimate,
        authorized, work-related purposes connected to the Sponsoring Business.
      </p>
      <p>
        You must not send spam, unlawful marketing, harassment, threats, discriminatory content, deceptive messages,
        offensive content, private solicitations, or communications outside the scope of your authorized role. You must
        not send marketing or promotional messages unless the Sponsoring Business has authorized them and any legally
        required consent has been obtained.
      </p>
      <p>
        <strong>Delivery is not guaranteed.</strong> Message delivery may be delayed or blocked by carriers, spam filters,
        device settings, network outages, incorrect contact information, or third-party provider issues. AAI is not liable
        for delayed, failed, misdirected, filtered, duplicated, or undelivered messages.
      </p>

      <h3 id="content">14) Content You Provide; Photos, Notes, Descriptions, and Messages</h3>
      <p>
        If you upload, enter, send, or create content through the Platform, including photos, notes, service descriptions,
        appointment records, messages, comments, images, forms, or profile content, you represent that you have all rights
        and permissions needed to do so and that the content is accurate, lawful, non-deceptive, non-infringing,
        respectful, and appropriate for the Platform.
      </p>
      <p>
        You grant AAI a limited, non-exclusive, worldwide license to host, store, process, transmit, display, reproduce,
        and use that content as reasonably necessary to operate, secure, support, troubleshoot, improve, and enforce the
        Platform and this Agreement.
      </p>
      <p>
        AAI may remove, restrict, preserve, or moderate content at any time if it creates legal, privacy, payment,
        security, reputational, customer-safety, or platform risk.
      </p>

      <h3 id="prohibited-conduct">15) Prohibited Conduct</h3>
      <p>You must not use the Platform to:</p>
      <ul>
        <li>
          violate any law, regulation, licensing rule, professional standard, privacy rule, payment rule, labor rule,
          consumer protection rule, or Sponsoring Business policy;
        </li>
        <li>provide Services you are not authorized, licensed, certified, trained, insured, or legally permitted to provide;</li>
        <li>
          harass, threaten, discriminate, stalk, exploit, abuse, intimidate, or retaliate against Customers, Businesses,
          staff, AAI, or other users;
        </li>
        <li>
          misrepresent credentials, identity, availability, service status, pricing, appointment status, completion
          status, or Customer communications;
        </li>
        <li>
          enter false records, alter records improperly, manipulate reviews, conceal errors, falsify appointments, or
          misuse scheduling tools;
        </li>
        <li>
          misuse Customer Information, contact Customers outside authorized channels, or solicit Customers for unauthorized
          purposes;
        </li>
        <li>send spam, malware, phishing attempts, deceptive content, unlawful marketing, or offensive communications;</li>
        <li>scrape, reverse engineer, resell, sublicense, copy, disrupt, overload, probe, bypass, or interfere with the Platform;</li>
        <li>
          attempt unauthorized access to accounts, systems, source code, databases, credentials, payment systems, or
          non-public Platform information;
        </li>
        <li>
          circumvent required booking flows, payment rules, processor rules, security controls, rate limits, permissions,
          or enforcement actions; or
        </li>
        <li>
          use the Platform in a way that creates risk for AAI, Customers, Businesses, Professionals, Service Providers,
          Payment Processors, or Platform stability.
        </li>
      </ul>
      <p>
        AAI may restrict, suspend, terminate, preserve evidence, cooperate with the Sponsoring Business or Payment
        Processors, and report suspected illegal activity to authorities where appropriate.
      </p>

      <h3 id="third-party-services">16) Third-Party Services and Integrations</h3>
      <p>
        The Platform may depend on or connect to third-party services, including hosting providers, email providers, SMS
        providers, analytics tools, maps, browsers, device operating systems, Payment Processors, and other integrations.
      </p>
      <p>
        Third-party services are controlled by their own providers and may be subject to separate terms, fees, restrictions,
        outages, changes, and privacy practices. AAI is not responsible for third-party services, third-party content,
        third-party outages, third-party pricing, third-party decisions, third-party data practices, or your compliance
        with third-party terms.
      </p>

      <h3 id="availability">17) Availability, Maintenance, and No Uptime Commitment</h3>
      <p>
        The Platform may be unavailable, slow, incomplete, inaccurate, or interrupted due to maintenance, upgrades, bugs,
        capacity limits, data migration, security work, hosting issues, internet outages, communication provider issues,
        Payment Processor outages, or other events outside AAI&apos;s control.
      </p>
      <p>
        AAI does not guarantee uninterrupted service, error-free service, data availability, message delivery, payment
        availability, booking availability, or any specific uptime percentage unless a separate written agreement signed
        by AAI expressly states otherwise.
      </p>
      <p>
        You and the Sponsoring Business are responsible for maintaining backup procedures and manual alternatives
        appropriate for your work, including offline schedules, customer contact procedures, payment reconciliation
        processes, and appointment confirmation methods.
      </p>

      <h3 id="disclaimers">18) Disclaimers; As-Is Platform</h3>
      <p>
        To the maximum extent permitted by law, the Platform, including AACore Booking, is provided &ldquo;as is,&rdquo;
        &ldquo;as available,&rdquo; &ldquo;with all faults,&rdquo; and &ldquo;at your own risk.&rdquo;
      </p>
      <p>
        AAI disclaims all warranties, whether express, implied, statutory, or otherwise, including warranties of
        merchantability, fitness for a particular purpose, title, non-infringement, accuracy, reliability, availability,
        security, uninterrupted use, error-free operation, compatibility, and any warranty arising from course of dealing or
        usage of trade.
      </p>
      <p>
        AAI does not warrant that the Platform will meet your requirements, satisfy the Sponsoring Business&apos;s
        requirements, comply with laws applicable to your specific services, prevent missed appointments, prevent
        double-booking, prevent payment disputes, prevent user error, preserve your employment or contractor relationship,
        or satisfy any particular business or professional outcome.
      </p>
      <p>
        AAI is not responsible for lost revenue, lost compensation, lost tips, lost Customers, lost goodwill, reputational
        harm, business interruption, missed appointments, failed communications, payment failures, service defects, Customer
        dissatisfaction, Sponsoring Business decisions, staff misconduct, or user configuration errors.
      </p>

      <h3 id="suspension-termination">19) Suspension and Termination of Professional Access</h3>
      <p>
        Your access may be suspended, limited, disabled, or terminated by the Sponsoring Business at any time. AAI may
        also suspend, limit, disable, or terminate your account or specific features immediately if:
      </p>
      <ul>
        <li>you violate this Agreement or Applicable Law;</li>
        <li>your use creates legal, payment, security, reputational, operational, customer-safety, or compliance risk;</li>
        <li>
          fraud, abuse, excessive disputes, illegal activity, unauthorized access, harassment, or misuse of Customer
          Information is suspected;
        </li>
        <li>
          a Sponsoring Business, Service Provider, Payment Processor, court, regulator, or law enforcement request requires
          or encourages action;
        </li>
        <li>
          your account information, identity, role, credentials, service activity, or Platform activity appears inaccurate
          or misleading; or
        </li>
        <li>your use harms AAI, Customers, Businesses, Professionals, Service Providers, Payment Processors, or Platform stability.</li>
      </ul>
      <p>
        Upon termination or loss of authorization, you must stop using the Platform and must not retain Customer
        Information obtained through the Platform unless independently authorized by the Sponsoring Business and Applicable
        Law. Termination does not entitle you to damages, credits, compensation, continued access, reinstatement, or
        reimbursement from AAI.
      </p>

      <h3 id="intellectual-property">20) Intellectual Property</h3>
      <p>
        AAI owns all rights, title, and interest in and to the Platform, including software, user interfaces, designs,
        workflows, documentation, databases, code, trade secrets, trademarks, service marks, domain names, templates,
        reports, analytics, know-how, and related intellectual property.
      </p>
      <p>
        During your authorized access period, AAI grants you a limited, revocable, non-exclusive, non-transferable,
        non-sublicensable license to access and use the Platform only as authorized by the Sponsoring Business and subject
        to this Agreement.
      </p>
      <p>
        You may not copy, resell, lease, sublicense, distribute, modify, create derivative works of, reverse engineer,
        decompile, benchmark publicly, or exploit the Platform except as expressly permitted by law or by written permission
        from AAI.
      </p>

      <h3 id="confidentiality-feedback">21) Confidentiality and Feedback</h3>
      <p>
        You agree not to disclose AAI confidential information, including non-public pricing, security details, roadmap
        information, technical design, workflows, screenshots of non-public features, account configuration methods, beta
        features, or system information, except as authorized by AAI or the Sponsoring Business and only to persons who
        need to know and are bound by appropriate confidentiality obligations.
      </p>
      <p>
        If you provide suggestions, feedback, ideas, enhancement requests, or recommendations, AAI may use them without
        restriction, payment, attribution, or obligation to you, unless a separate written agreement signed by AAI states
        otherwise.
      </p>

      <h3 id="indemnification">22) Indemnification</h3>
      <p>
        You agree to defend, indemnify, and hold harmless Company Parties from and against all claims, demands, actions,
        investigations, damages, liabilities, fines, penalties, losses, settlements, costs, and expenses, including
        reasonable attorneys&apos; fees, arising out of or related to:
      </p>
      <ul>
        <li>
          your services, conduct, communications, negligence, malpractice, omissions, customer interactions, service
          outcomes, injuries, dissatisfaction, or professional judgment;
        </li>
        <li>
          your lack of authorization, training, licensing, certification, insurance, permits, or legal qualification to
          provide Services;
        </li>
        <li>
          your violation of Applicable Law, professional standards, privacy rules, payment rules, consumer protection rules,
          labor rules, tax obligations, or Sponsoring Business policies;
        </li>
        <li>
          your misuse of Customer Information, unauthorized disclosure, improper retention, unauthorized solicitation, or
          privacy violation;
        </li>
        <li>
          payment disputes, tip disputes, compensation disputes, chargebacks, refunds, reversals, payout issues, tax issues,
          identity verification, prohibited activity, or Payment Processor actions related to your conduct or Services;
        </li>
        <li>
          disputes between you and Customers, the Sponsoring Business, staff, contractors, owners, vendors, Payment
          Processors, or other third parties;
        </li>
        <li>
          your content, notes, messages, images, intellectual property infringement, false statements, or improper
          collection or use of sensitive data;
        </li>
        <li>
          your misuse of the Platform, account compromise caused by your side, security failures, misconfiguration, user
          error, credential sharing, or unauthorized account use; and
        </li>
        <li>your breach of this Agreement or representations made to AAI.</li>
      </ul>

      <h3 id="limitation-liability">23) Limitation of Liability</h3>
      <p>
        To the maximum extent permitted by law, Company Parties will not be liable for indirect, incidental, special,
        consequential, exemplary, punitive, enhanced, or similar damages, including lost profits, lost revenue, lost
        compensation, lost tips, lost Customers, lost goodwill, reputational harm, loss of data, business interruption,
        service interruption, missed appointments, failed messages, payment failure, chargebacks, refunds, processor holds,
        or cost of substitute services, even if advised of the possibility of such damages.
      </p>
      <p>
        To the maximum extent permitted by law, Company Parties are not liable for outages, defects, trial-period
        limitations, beta or enhancement-period issues, third-party failures, Payment Processor actions, data loss,
        security incidents not caused solely by AAI&apos;s willful misconduct, Customer disputes, Sponsoring Business
        decisions, employment or contractor disputes, wage or tip disputes, user error, configuration mistakes, missed
        appointments, or communications failures.
      </p>
      <p>
        AAI&apos;s total aggregate liability for all claims related to the Platform or this Agreement will not exceed the
        greater of: (a) the fees paid directly to AAI for your individual Professional access during the three (3) months
        before the event giving rise to the claim; or (b) one hundred dollars ($100), unless a higher amount is required by
        law and cannot be limited by contract.
      </p>
      <p>
        For free, trial, beta, early access, or enhancement-period use, you acknowledge that AAI is providing access
        without subscription charge and that this limitation of liability is a fundamental condition of that free access.
      </p>

      <h3 id="release">24) Release</h3>
      <p>To the maximum extent permitted by law, you release Company Parties from claims arising out of or related to:</p>
      <ul>
        <li>
          your services, conduct, appointments, Customers, Professional Data, Customer Information, business decisions, and
          relationship with the Sponsoring Business;
        </li>
        <li>
          Customer complaints, injuries, cancellations, refunds, disputes, chargebacks, dissatisfaction, allergic reactions,
          infections, negligence claims, malpractice claims, or service outcomes;
        </li>
        <li>
          Sponsoring Business decisions, account permissions, scheduling decisions, payroll decisions, compensation
          decisions, tips, termination, discipline, role changes, customer assignments, and policy decisions;
        </li>
        <li>
          Payment Processor actions, Stripe-related account actions, processor downtime, withheld funds, payout delays,
          reversals, reserves, identity verification, and dispute outcomes;
        </li>
        <li>
          third-party outages involving hosting, internet, email, SMS, carriers, browsers, devices, operating systems, app
          stores, maps, Payment Processors, or other providers; and
        </li>
        <li>
          your configuration decisions, data entry, user error, credentials, devices, and failure to maintain backup
          records.
        </li>
      </ul>

      <h3 id="force-majeure">25) Force Majeure</h3>
      <p>
        AAI is not responsible for delays, failures, data loss, unavailability, or inability to perform caused by events
        beyond its reasonable control, including natural disasters, fire, flood, weather, power failures, internet outages,
        carrier outages, hosting failures, Payment Processor failures, cyberattacks, malware, pandemics, war, terrorism,
        civil unrest, labor disputes, supply chain issues, government actions, legal restrictions, or vendor failures.
      </p>

      <h3 id="dispute-resolution">26) Dispute Resolution; Arbitration; Class Action Waiver</h3>
      <p>
        <strong>Informal first.</strong> Before filing a claim, you agree to contact{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a> and allow thirty (30) days for AAI to attempt
        to resolve the matter informally.
      </p>
      <p>
        <strong>Arbitration.</strong> Except for small claims matters or claims for injunctive relief, disputes arising
        from or related to this Agreement or the Platform will be resolved by binding individual arbitration in Virginia or
        remotely by video where appropriate, under applicable arbitration rules.
      </p>
      <p>
        <strong>Class action waiver.</strong> Disputes must be brought individually and not as a class, collective,
        consolidated, private attorney general, or representative action, to the maximum extent permitted by law.
      </p>
      <p>
        <strong>Injunctive relief.</strong> Either party may seek injunctive or equitable relief in court for intellectual
        property misuse, confidentiality violations, security threats, unauthorized access, payment fraud, misuse of
        Customer Information, or misuse of the Platform.
      </p>

      <h3 id="governing-law">27) Governing Law; Venue</h3>
      <p>
        Virginia law governs this Agreement and any dispute related to the Platform, excluding conflict-of-law rules. If a
        dispute is not subject to arbitration, it will be brought exclusively in the state or federal courts located in
        Virginia, and you consent to personal jurisdiction and venue in those courts.
      </p>

      <h3 id="changes-agreement">28) Changes to Agreement</h3>
      <p>
        AAI may update this Agreement from time to time. Continued use of the Platform after updates become effective
        constitutes acceptance of the updated Agreement. If changes are material, AAI will provide reasonable notice through
        the Platform, email, account notice, or other reasonable method.
      </p>
      <p>If you do not agree to an updated Agreement, you must stop using the Platform.</p>

      <h3 id="miscellaneous-contact">29) Miscellaneous; Contact</h3>
      <p>
        <strong>Assignment.</strong> You may not assign this Agreement without AAI&apos;s prior written consent. AAI may
        assign this Agreement in connection with a merger, acquisition, reorganization, financing, sale of assets,
        corporate transaction, change of control, or transfer of the Platform.
      </p>
      <p>
        <strong>Severability.</strong> If any provision is unenforceable, the remaining provisions will remain in effect,
        and the unenforceable provision will be modified to the minimum extent necessary to make it enforceable where
        possible.
      </p>
      <p>
        <strong>No waiver.</strong> AAI&apos;s failure to enforce a provision is not a waiver of its right to enforce that
        provision later.
      </p>
      <p>
        <strong>Headings.</strong> Headings are for convenience only and do not limit the Agreement.
      </p>
      <p>
        <strong>Entire agreement.</strong> This Agreement and the Privacy Policy are the complete agreement between you
        and AAI regarding your Professional use of the Platform, except for any signed written agreement with AAI that
        expressly modifies this Agreement. This Agreement does not replace any separate agreement between you and the
        Sponsoring Business.
      </p>
      <p>
        <strong>Contact.</strong> A&amp;A Inomatic, LLC, Clifton, VA 20124. Email:{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>.
      </p>

      <div id="agreement-acknowledgment" className="rounded-lg border border-gray-200 bg-blue-50 px-4 py-3 text-sm text-gray-700 mt-8">
        <h3 className="mt-0">Agreement Acknowledgment</h3>
        <p>
          I acknowledge that my Professional account is invite-only and controlled by my Sponsoring Business. I agree to
          the AACore Professional / Service Provider Account Agreement and understand that AACore is a technology platform
          provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; that AACore Booking is free during the enhancement
          and trial period through December 31, 2026 unless changed by AAI, and that I use the Platform at my own risk.
        </p>
        <p className="mb-0">
          I further acknowledge that AAI does not employ, supervise, pay, license, insure, schedule, or control me or my
          Services, and that AAI is not Stripe, is not affiliated with or endorsed by Stripe, is not a bank or payment
          processor, does not hold full payment card or banking information, does not control Customer funds, Business
          payouts, Professional pay, or tips, and is not responsible for my conduct, services, licensing, customer
          interactions, compensation, taxes, chargebacks, Sponsoring Business policies, or legal compliance.
        </p>
      </div>
    </AgreementPageLayout>
  );
};

export default ProfessionalAccountAgreementPage;
